import { PayrollDriverRow } from "@/lib/api/company/timesheet/groupedTimesheet/types"
import { CellKey } from "./types"
import { UNCLOSED_KEEPS_APPLIED_REWARD } from "./constants"

type MonthRange = {
  startDate: Date;
  endDate: Date;
};

/**
 * month: 1-12
 */
export function getMonthDateRange(year: number, month: number): MonthRange {
  const startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const endDate = new Date(year, month, 0, 23, 59, 59, 999);

  return { startDate, endDate };
}

export const removeKey = <T extends Record<string, unknown>>(obj: T, key: string) => {
  const { [key]: _removed, ...rest } = obj
  return rest as Omit<T, typeof key> as T
}

export const toNumberOrUndefined = (raw: string) => {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Baza trzyma kwoty jako decimal(18,2), a oczekiwane netto z BFM potrafi miec wiecej miejsc
 * po przecinku. Porownujemy z tolerancja grosza - rownosc po zaokragleniu nie wystarcza,
 * bo klient zaokragla liczbe zmiennoprzecinkowa, a serwer dokladny decimal, i te dwa
 * zaokraglenia potrafia sie rozjechac o jeden grosz.
 *
 * UWAGA: uzywane takze do decyzji, ktorzy kierowcy ida do przebiegu adopcji nagrody
 * (useSaveAndCalculate) - zmiana tolerancji zmienia KWOTY, nie tylko wyswietlanie.
 */
export const rozniSie = (a: number, b: number) => Math.abs(a - b) > 0.011

/**
 * Kwota, ktora w kolumnie WYLICZONE NETTO widzi uzytkownik - i jednoczesnie ta, ktora
 * solver nagrody faktycznie domyka do oczekiwanego netto (GrossSalaryCarlculator.
 * GetNettoAmountForStep zwraca FinalNettoAmount).
 *
 * finalNettoAmount = calculatedNetto + dodatki - potracenia - komornik (+ wymuszone
 * urlop/chorobowe netto). WPF bindowal w tej kolumnie wlasnie FinalNettoAmount
 * (DriverTimesheetGroupView.xaml), a web pokazywal surowe calculatedNetto sprzed
 * potracen - u kierowcy z komornikiem kolumna klamala, a getNettoMismatch zapalal
 * ostrzezenie tam, gdzie WPF go nie ma.
 *
 * UWAGA: calculatedNetto zostaje w DTO nietkniete, bo finalNettoAmountDescription
 * buduje z niego rozbicie "netto - potracenia = final" i potrzebuje surowej kwoty.
 */
export const getDisplayedNetto = (row: PayrollDriverRow): number | null =>
  row.finalNettoAmount ?? row.calculatedNetto

/**
 * Czy zapisane "wyliczone netto" nie domknelo sie do zapisanego "oczekiwanego netto".
 *
 * Jeden warunek lapie oba znane przypadki: solver, ktory sie poddal i wstawil wartosc
 * awaryjna (GrossSalaryCarlculator.Calculate), oraz rozjazd o kwote urlopu. Nie musimy ich
 * rozrozniac - objawem jest zawsze niedomkniete netto.
 *
 * Porownujemy ZAPISANE z ZAPISANYM (row.*), nigdy z biezaca edycja w polu - inaczej samo
 * wpisanie nowego celu zapalaloby ostrzezenie o przeliczeniu, ktore jeszcze nie ruszylo.
 */
export const getNettoMismatch = (row: PayrollDriverRow): number | null => {
  const expected = row.expectedNettoAmount
  const calculated = getDisplayedNetto(row)

  if (expected == null || expected <= 0) return null
  if (calculated == null) return null
  if (!rozniSie(calculated, expected)) return null

  return calculated - expected
}

/**
 * To samo, ale do WYSWIETLENIA - bez przypadku, ktory umiemy wytlumaczyc.
 *
 * Gdy "Wliczaj" jest zaznaczone, a wyliczone netto przewyzsza cel DOKLADNIE o kwote delegacji
 * krajowych, to nie jest zagadka: cel zostal obnizony o delegacje, a pensja nie schodzi tak
 * nisko (solver nie umie zejsc ponizej podstawy, wiec oddaje wartosc awaryjna). WPF w tej
 * sytuacji milczy - ostrzega wylacznie przy NIEDOWIEZIENIU celu
 * (IsNettoOutOfDate => FinalNettoAmount < ExpectedNettoAmount), nigdy przy przestrzeleniu.
 *
 * ROZDZIELONE OD getNettoMismatch SWIADOMIE: tamta wersja steruje pickReward, czyli KWOTAMI.
 * Gdyby ikona i wybor nagrody szly z jednej funkcji, samo wyciszenie znacznika przestawialoby
 * nagrode z nalozonej na propozycje solvera (tu: z 2084,80 na 0) i cicho zmienialo wyplate.
 */
export const getNettoMismatchForDisplay = (
  row: PayrollDriverRow,
): number | null => {
  const mismatch = getNettoMismatch(row)
  if (mismatch == null) return null

  const trips = row.polishTripsAmount ?? 0
  if (!row.includeDomesticBusinessTrip || trips === 0) return mismatch
  if (!rozniSie(mismatch, trips)) return null

  return mismatch
}


export const isDomesticTripIncluded = (
  row: PayrollDriverRow,
  edit: boolean | undefined,
): boolean => edit ?? Boolean(row.includeDomesticBusinessTrip)

/**
 * Kwota OCZEKIWANE NETTO sprzed odjecia delegacji krajowych, do pokazania w nawiasie obok
 * celu. Zwraca null, gdy nie ma czego pokazywac (brak "Wliczaj" albo zerowe delegacje).
 *
 * Zaznaczenie "Wliczaj" nadpisuje wpisana kwote celu (odejmuje od niej delegacje - tak samo
 * jak przycisk "Odejmij od oczekiwanej kwoty netto" w WPF), wiec bez tego uzytkownik traci
 * z oczu liczbe, ktora sam wpisal.
 *
 * `captured` to kwota zapamietana w momencie zaznaczenia - dokladna. Po przeladowaniu strony
 * jej nie ma i kwote trzeba ODTWORZYC jako cel + delegacje. Odtworzenie klamie w trzech
 * przypadkach: gdy odjecie zostalo przyciete do zera (delegacje >= cel), gdy delegacje
 * zmienily sie po odjeciu (PolishTripsAmount jest nadpisywany przy kazdym przeliczeniu)
 * i gdy w pasku wybrano tryb daty delegacji (kwota liczona na zywo, nie ta odjeta).
 * Dlatego wywolujacy powinien odroznic w dymku kwote zapamietana od odtworzonej.
 */
export const getExpectedNettoBeforeDomesticTrips = (
  row: PayrollDriverRow,
  edits: {
    expectedNetto?: number
    includeBusinessTrip?: boolean
    captured?: number
  },
): number | null => {
  const include =
    edits.includeBusinessTrip ?? Boolean(row.includeDomesticBusinessTrip)
  const trips = row.polishTripsAmount ?? 0

  if (!include || trips === 0) return null
  if (edits.captured != null) return edits.captured

  return (edits.expectedNetto ?? row.expectedNettoAmount ?? 0) + trips
}

/**
 * Ktora nagroda rzadzi w tym wierszu: propozycja solvera czy nagroda zapisana na ewidencji.
 * Uzywane i do wyswietlania w kolumnie NAGRODA, i do budowy ladunku "Przelicz" - te dwa
 * miejsca musza dawac te sama odpowiedz, inaczej grid pokazuje co innego niz wysyla.
 * Regula: patrz UNCLOSED_KEEPS_APPLIED_REWARD.
 */
export const pickReward = (row: PayrollDriverRow): number => {
  const applied = row.reward != null ? Number(row.reward) : null
  const proposal = row.calculatedBonus != null ? Number(row.calculatedBonus) : null

  if (UNCLOSED_KEEPS_APPLIED_REWARD && getNettoMismatch(row) != null) {
    return applied ?? proposal ?? 0
  }

  return proposal ?? applied ?? 0
}

/**
 * MANDAT i ZALICZKA nie sa czescia PayrollDriverRow - przychodza osobno z EasyTMS/BFM
 * i grid trzyma je w mapie po driverId. Zeby dalo sie po nich sortowac, compare()
 * dostaje te mape z zewnatrz.
 */
export type PayrollSortExtras = {
  mandatesAdvancesByDriverId?: Record<string, { mandat: number; zaliczka: number }>
}

const getSortValue = (
  row: PayrollDriverRow,
  field: CellKey,
  extras?: PayrollSortExtras,
): string | number => {
  const mandatesAdvances = extras?.mandatesAdvancesByDriverId?.[String(row.driver.id)]

  switch (field) {
    case 'fullName':
      return row.driver.fullName ?? ''
    case 'calculatedNetto':
      return getDisplayedNetto(row) ?? Number.NEGATIVE_INFINITY
    case 'expectedNetto':
      return row.expectedNettoAmount ?? Number.NEGATIVE_INFINITY
    case 'offPay':
      return row.leavesGrossAmount ?? Number.NEGATIVE_INFINITY
    case 'sickPay':
      return row.sickLeavesGrossAmount ?? Number.NEGATIVE_INFINITY
    case 'benefits':
      return row.benefitsGrossAmount ?? Number.NEGATIVE_INFINITY
    case 'bailiffDeduction':
      return row.bailiffDeductionAmount ?? Number.NEGATIVE_INFINITY
    case 'costs':
      // Kolumna KOSZTY pokazuje tripCosts z fallbackiem na additionAmount (patrz
      // PayrollResultsTableRow) - sortowanie musi brac dokladnie te sama kwote.
      return row.tripCosts ?? row.additionAmount ?? Number.NEGATIVE_INFINITY
    // case 'deductionAmount':
    //   return row.deductionAmount ?? Number.NEGATIVE_INFINITY
    case 'businessTrip':
      return row.polishTripsAmount ?? Number.NEGATIVE_INFINITY
    case 'bonus':
      return row.bonus ?? Number.NEGATIVE_INFINITY
    case 'reward':
      return row.calculatedBonus ?? row.reward ?? Number.NEGATIVE_INFINITY
    case 'summary':
      // BRUTTO (LACZNIE) to row.summary - wczesniej stal tu row.bonus (kolumna PREMIA),
      // wiec kolumna sortowala sie po cudzych kwotach.
      return row.summary ?? Number.NEGATIVE_INFINITY
    case 'mandate':
      return mandatesAdvances?.mandat ?? Number.NEGATIVE_INFINITY
    case 'advance':
      return mandatesAdvances?.zaliczka ?? Number.NEGATIVE_INFINITY
    default:
      return ''
  }
}

export const compare = (
  a: PayrollDriverRow,
  b: PayrollDriverRow,
  field: CellKey,
  asc: boolean,
  extras?: PayrollSortExtras,
) => {
  const av = getSortValue(a, field, extras)
  const bv = getSortValue(b, field, extras)

  // String compare
  if (typeof av === 'string' || typeof bv === 'string') {
    const as = String(av)
    const bs = String(bv)
    const res = as.localeCompare(bs, 'pl', { sensitivity: 'base' })
    return asc ? res : -res
  }

  // Number compare (handles -Infinity)
  const res = av - bv
  return asc ? res : -res
}

/** Kwota z gridu w postaci liczby: brak wartosci (null/undefined/pusty string) znaczy 0. */
const toAmount = (value: unknown): number => {
  if (value == null || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Czy wpis w mapie edycji rzeczywiscie zmienia zapisana kwote.
 *
 * MoneyInput emituje onChange takze przy samym WYJSCIU z pola (handleBlur), wiec klikniecie
 * w komorke i klikniecie obok wpisuje do mapy edycji wartosc identyczna z zapisana. Sama
 * obecnosc klucza NIE jest wiec dowodem zmiany - to dlatego "Przelicz zmodyfikowanych"
 * pokazywalo sie po samym zaznaczeniu liczby w komorce.
 *
 * Porownanie liczbowe z tolerancja grosza (rozniSie), bo baza oddaje decimal (czasem jako
 * string), a wartosc z pola przechodzi przez Number() - strict !== zapalalby roznice tam,
 * gdzie kwota jest ta sama.
 */
export const isAmountEdited = (
  edited: number | undefined,
  saved: unknown,
): boolean => typeof edited === 'number' && rozniSie(edited, toAmount(saved))

/** Niezapisane edycje jednego wiersza gridu (klucz driverId w mapach GroupPayrollView). */
export type PayrollRowEdits = {
  offPay?: number
  sickPay?: number
  benefits?: number
  bonus?: number
  expectedNetto?: number
  reward?: number
  finalLeaveHours?: number
  includeBusinessTrip?: boolean
  leaveAdditions?: Record<number, number>
}

/**
 * Ktore pola wiersza uzytkownik faktycznie zmienil. Jedno zrodlo prawdy dla czerwonego
 * oznaczenia komorek w gridzie i dla licznika przy "Przelicz zmodyfikowanych" - inaczej
 * grid pokazywalby na czerwono wiersz, ktorego przycisk nie liczy (albo odwrotnie).
 */
export const getEditedFields = (
  row: PayrollDriverRow,
  edits: PayrollRowEdits,
) => {
  const leaveAdditionsDirty = Object.entries(edits.leaveAdditions ?? {}).some(
    ([dayType, amount]) => {
      const entry = row.timesheetAdditionForLeaves?.find(
        (e) => e.dayType === Number(dayType),
      )
      return isAmountEdited(amount, entry?.amount)
    },
  )

  return {
    offPayDirty: isAmountEdited(edits.offPay, row.leavesGrossAmount),
    sickPayDirty: isAmountEdited(edits.sickPay, row.sickLeavesGrossAmount),
    benefitsDirty: isAmountEdited(edits.benefits, row.benefitsGrossAmount),
    bonusIsDirty: isAmountEdited(edits.bonus, row.bonus),
    expectedNettoDirty: isAmountEdited(
      edits.expectedNetto,
      row.expectedNettoAmount,
    ),
    rewardDirty: isAmountEdited(edits.reward, pickReward(row)),
    finalLeaveHoursDirty: isAmountEdited(
      edits.finalLeaveHours,
      row.finalLeavesGrossHours,
    ),
    includeBusinessTripDirty:
      typeof edits.includeBusinessTrip === 'boolean' &&
      edits.includeBusinessTrip !== Boolean(row.includeDomesticBusinessTrip),
    leaveAdditionsDirty,
  }
}

/** Czy w wierszu jest jakakolwiek niezapisana zmiana wartosci. */
export const isRowEdited = (row: PayrollDriverRow, edits: PayrollRowEdits) =>
  Object.values(getEditedFields(row, edits)).some(Boolean)
